// Public item-to-item discovery only. No account state, favourites or history.
export function similarCandidates(value, currentId) {
  if (value?.version !== 1 || value.available !== true || !Array.isArray(value.items)) throw new Error('similar_unavailable');
  const seen = new Set([String(currentId)]);
  return value.items.filter(item => {
    const id = String(item?.id || '');
    if (!/^\d+$/.test(id) || seen.has(id) || item.available !== true || !/^[a-z0-9][a-z0-9-]*$/.test(item.handle || '')) return false;
    seen.add(id); return true;
  }).slice(0, 6);
}
export async function readSimilarProducts(id, { fetcher = fetch, signal } = {}) {
  if (!/^\d+$/.test(String(id))) throw new Error('invalid_product');
  const response = await fetcher(`/apps/sigma-stock-alerts/for-you/v1/similar?product_id=${id}`, {
    method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal,
  });
  if (!response.ok) throw new Error('similar_unavailable');
  return similarCandidates(await response.json(), id);
}
async function enhance(section) {
  if (section.dataset.similarStarted) return;
  section.dataset.similarStarted = 'true';
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 12000);
  const stop = () => controller.abort();
  window.addEventListener('pagehide', stop, { once: true });
  const root = section.closest('[data-product-section]');
  const rail = section.querySelector('.coffee-product__discovery-rail');
  try {
    const items = await readSimilarProducts(section.dataset.productSimilar, { signal: controller.signal });
    const cards = Array(items.length).fill(null);
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(3, items.length) }, async () => {
      while (next < items.length && !controller.signal.aborted) {
        const index = next++, item = items[index];
        try {
          const response = await fetch(`${section.dataset.similarRoot || '/'}products/${item.handle}?view=similar-coffee-card`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
          if (!response.ok) continue;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = doc.querySelector('[data-product-similar-card-id]');
          if (source?.dataset.productSimilarCardId !== String(item.id) || source.dataset.available !== 'true') continue;
          const card = source.querySelector('.cart-empty__product');
          if (card) cards[index] = card.cloneNode(true);
        } catch { /* Keep other fresh alternatives; never insert incomplete markup. */ }
      }
    }));
    if (controller.signal.aborted || !section.isConnected || section.contains(document.activeElement)) return;
    const ready = cards.filter(Boolean);
    // A transport/HTML failure retains the already useful server-rendered row.
    if (items.length && !ready.length) return;
    rail.replaceChildren(...ready);
    section.hidden = ready.length === 0;
    section.dataset.similarSource = 'catalog';
    section.querySelector('h2').textContent = 'Similar coffees';
    section.querySelector('[data-product-discovery-viewport]').setAttribute('aria-label', 'Similar coffees');
    if (ready.length) root.querySelector('[data-product-discovery]').hidden = false;
    else if (![...root.querySelectorAll('[data-product-discovery-section]')].some(node => !node.hidden)) root.querySelector('[data-product-discovery]').hidden = true;
    section.dispatchEvent(new CustomEvent('sigma:product-discovery:updated', { bubbles: true }));
  } catch { /* The original Liquid similarity row remains the progressive fallback. */ }
  finally { clearTimeout(timer); window.removeEventListener('pagehide', stop); }
}
function initialize(scope = document) {
  scope.querySelectorAll('[data-product-similar]').forEach(section => {
    if (section.dataset.similarObserved) return;
    section.dataset.similarObserved = 'true';
    const root = section.closest('[data-product-section]');
    if (typeof IntersectionObserver !== 'function') { void enhance(section); return; }
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect(); void enhance(section);
    }, { rootMargin: '600px 0px' });
    observer.observe(section.hidden ? root.querySelector('[data-product-description]') || root : section);
    window.addEventListener('pagehide', () => observer.disconnect(), { once: true });
  });
}
if (typeof document !== 'undefined') {
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
}
